import { TradeInPreviewAcquisition } from '@carvana/merch-contracts';
import { useCallback, useContext, useEffect, useRef } from 'react';
import COOKIES from '~/constants/cookies';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import StaticContext from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import useCommitTrades from '~/hooks/common/services/useCommitTrades';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import fetchTradeInPreviewAcquisitions from '~/services/common/fetchTradeInPreviewAcquisitions';
import { BrowserCookies } from '~/utilities';
import selectPreferredTrade from '~/utilities/common/selectPreferredTrade';
import { generateErrorLogger, logInfo } from '~/utilities/loggers';

export default function useGetTrades(
  isLoggedIn: boolean,
  preferredAcquisitionNameFromQueryParam?: string
) {
  const {
    fetchTradesFlag,
    trades: { setError, setLoaded, setLoading }
  } = useContext(TradeContext);
  const { eventCorrelationId } = useContext(StaticContext);
  const { trades: tradesResolvedOnServer } = useResolvedOnServer();

  const commit = useCommitTrades();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    tradesResolvedOnServer
  );

  const queryParamAppliedRef = useRef<string | undefined>(undefined);

  const handleMultipleTrades = useCallback(
    (trades: TradeInPreviewAcquisition[]) => {
      const shouldApplyQueryParam =
        Boolean(preferredAcquisitionNameFromQueryParam) &&
        queryParamAppliedRef.current !== preferredAcquisitionNameFromQueryParam;

      const cookieId = BrowserCookies.get(COOKIES.preferredTrade) ?? '';
      const selectedTrade = selectPreferredTrade(
        trades,
        shouldApplyQueryParam ? preferredAcquisitionNameFromQueryParam : '',
        cookieId
      );

      if (shouldApplyQueryParam) {
        if (selectedTrade?.id === preferredAcquisitionNameFromQueryParam) {
          queryParamAppliedRef.current = preferredAcquisitionNameFromQueryParam;
          logInfo('Trade query param applied', {
            preferredAcquisitionName: preferredAcquisitionNameFromQueryParam
          });
        } else {
          logInfo('Trade acquisition name mismatch from query param', {
            availableTradeCount: trades.length,
            paramPreferredAcquisitionName:
              preferredAcquisitionNameFromQueryParam
          });
        }
      }

      commit({ selectedTrade, trades });
    },
    [commit, preferredAcquisitionNameFromQueryParam]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      setLoaded();

      return;
    }

    if (shouldSkipFetch()) return;

    setLoading();
    fetchTradeInPreviewAcquisitions(eventCorrelationId)
      .then(trades => {
        if (trades.length > 1) {
          handleMultipleTrades(trades);
        } else {
          if (
            preferredAcquisitionNameFromQueryParam &&
            !trades.some(
              trade => trade.id === preferredAcquisitionNameFromQueryParam
            )
          ) {
            logInfo('Trade acquisition name mismatch from query param', {
              availableTradeCount: trades.length,
              paramPreferredAcquisitionName:
                preferredAcquisitionNameFromQueryParam
            });
          }

          commit({ selectedTrade: trades.at(0) ?? null, trades });
        }
      })
      .catch(generateErrorLogger('Get trades', {}, { callback: setError }));
  }, [
    commit,
    eventCorrelationId,
    fetchTradesFlag,
    handleMultipleTrades,
    isLoggedIn,
    preferredAcquisitionNameFromQueryParam,
    setError,
    setLoaded,
    setLoading,
    shouldSkipFetch
  ]);
}
